import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import API from '../api';
import mvolaLogo from '../image/mvola-logo.png';
import orangeLogo from '../image/orange-money.jpg';

const MVOLA_NUMBER_PATTERN = /^0\d{9}$/;

const getMvolaNumberError = (number) => {
  if (!number) return 'Veuillez entrer votre numéro MVola.';
  if (!/^\d+$/.test(number)) return 'Le numéro MVola doit contenir uniquement des chiffres.';
  if (!number.startsWith('0')) return 'Le numéro MVola doit commencer par 0.';
  if (number.length !== 10) return 'Le numéro MVola doit contenir exactement 10 chiffres.';
  return '';
};

const DepositPage = ({ user, setUser }) => {
  // States for deposit
  const [depositMGA, setDepositMGA] = useState('');
  const [depositNumber, setDepositNumber] = useState('');
  const [depositMGAError, setDepositMGAError] = useState('');
  const [mvolaNumberError, setMvolaNumberError] = useState('');
  const [depositMethod, setDepositMethod] = useState('manual'); // 'mvola', 'manual', or 'orange'
  const [depositState, setDepositState] = useState('idle'); // idle, processing, success, failed, timeout
  const [depositId, setDepositId] = useState(null);
  // 'manual' also supported for Mvola manuel method
  const [depositInitialMessage, setDepositInitialMessage] = useState('');
  const [depositResultMessage, setDepositResultMessage] = useState('');
  const [dropdownMethodOpen, setDropdownMethodOpen] = useState(false);
  const [methodStatus, setMethodStatus] = useState({ mvola: 1, orange: 1 });
  const [isManualAvailable, setIsManualAvailable] = useState(true);
  const [isSavingMvolaNumber, setIsSavingMvolaNumber] = useState(false);
  const savedMvolaNumber = user?.mvola_number || '';
  const needsMvolaNumberRegistration = depositMethod === 'mvola' && !savedMvolaNumber;

  // Effect for polling deposit status
  useEffect(() => {
    let intervalId = null;
    let timeoutId = null;

    const pollStatus = async () => {
      if (!depositId) return;
      try {
        const res = await API.get(`/deposits/${depositId}/`);
        const status = res.data.status;
        if (status === 'SUCCESS') {
          setDepositState('success');
          setDepositResultMessage('Transaction complétée avec succès.');
          setUser(prevUser => ({ ...prevUser, balance_mga: res.data.new_balance })); // Update MGA balance locally
          setDepositId(null); // Stop polling
        } else if (status === 'FAILED') {
          setDepositState('failed');
          const baseMessage = 'La transaction a échoué.';
          const reason = res.data.error_message ? ' Raison: ' + res.data.error_message : '';
          setDepositResultMessage(depositMethod === 'orange' ? baseMessage : baseMessage + reason);
          setDepositId(null); // Stop polling
        }
        // If still PENDING, do nothing, interval will poll again
      } catch (err) {
        console.error('Error polling deposit status:', err);
        // Optionally handle polling errors, maybe stop polling after too many errors
      }
    };

    if (depositState === 'processing' && depositId) {
      pollStatus(); 
      intervalId = setInterval(pollStatus, 5000);

      // Set a timeout (35s for MVola, 16min for Orange)
      const timeoutDuration = depositMethod === 'orange'
        ? 16 * 60 * 1000
        : depositMethod === 'manual'
        ? 15 * 60 * 60 * 1000
        : 35000;
      const timeoutMessage = depositMethod === 'orange'
        ? 'La transaction est toujours en attente après 16 minutes. Veuillez vérifier votre paiement Orange Money ou contacter le support.'
        : depositMethod === 'manual'
        ? 'Votre dépôt manuel est toujours en attente après 15 heures. Veuillez contacter le support si nécessaire.'
        : 'La transaction est toujours en attente après 35 secondes. Il pourrait y avoir un problème avec MVola. Veuillez vérifier votre solde MVola ou contacter le support.';
      timeoutId = setTimeout(() => {
        if (depositState === 'processing') {
           setDepositState('timeout');
           setDepositResultMessage(timeoutMessage);
           setDepositId(null);
        }
      }, timeoutDuration);
    }

    // Cleanup function
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [depositState, depositId, depositMethod, setUser]);

  // Add event listener to close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownMethodOpen) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.custom-dropdown')) {
        setDropdownMethodOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownMethodOpen]);

  // Fetch payment method status on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await API.get('/payment-method-status/');
        setMethodStatus(res.data || { mvola: 1, orange: 1 });
      } catch (e) {
        // Keep defaults if call fails
      }
    })();
  }, []);

  // Update manual availability (08h - 22h Antananarivo time, except Saturdays)
  useEffect(() => {
    const update = () => {
      const now = new Date();
      // Compute hour and weekday in Madagascar timezone to align with backend enforcement
      let mgHour;
      let mgWeekday;
      try {
        mgHour = parseInt(new Intl.DateTimeFormat('fr-MG', { timeZone: 'Indian/Antananarivo', hour: '2-digit', hour12: false }).format(now), 10);
        mgWeekday = new Intl.DateTimeFormat('en-US', { timeZone: 'Indian/Antananarivo', weekday: 'short' }).format(now);
      } catch (e) {
        // Fallback to local date values if timezone is not supported
        mgHour = now.getHours();
        mgWeekday = now.getDay() === 6 ? 'Sat' : '';
      }
      setIsManualAvailable(mgWeekday !== 'Sat' && mgHour >= 8 && mgHour < 22);
    };
    update();
    const id = setInterval(update, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Handler for deposit
  const handleSaveMvolaNumber = async () => {
    if (methodStatus.mvola === 0) {
      toast.error("Ce moyen de paiement est momentanément indisponible chez le fournisseur. Veuillez choisir une autre méthode de paiement.");
      return;
    }
    const normalizedNumber = depositNumber;
    const validationError = getMvolaNumberError(normalizedNumber);
    if (validationError) {
      setMvolaNumberError(validationError);
      toast.error(validationError);
      return;
    }

    try {
      setIsSavingMvolaNumber(true);
      const res = await API.post('/deposits/save-mvola-number/', { mvola_number: normalizedNumber });
      setUser(prevUser => ({ ...prevUser, mvola_number: res.data.mvola_number || normalizedNumber }));
      setDepositNumber('');
      setMvolaNumberError('');
      toast.success('Numéro MVola enregistré. Vous pouvez maintenant saisir le montant du dépôt.');
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error("Échec de l'enregistrement du numéro MVola");
      }
    } finally {
      setIsSavingMvolaNumber(false);
    }
  };

  const handleDeposit = async () => {
    if (depositMethod === 'manual' && !isManualAvailable) return;
    if ((depositMethod === 'mvola' && methodStatus.mvola === 0) || (depositMethod === 'orange' && methodStatus.orange === 0)) {
      toast.error("Ce moyen de paiement est momentanément indisponible chez le fournisseur. Veuillez choisir une autre méthode de paiement.");
      return;
    }
    if (depositMethod === 'mvola' && !savedMvolaNumber) {
      toast.error("Veuillez d'abord enregistrer votre numéro MVola.");
      return;
    }
    if (!depositMGA || (depositMethod === 'manual' && !depositNumber)) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    // For Orange Money, open a blank window immediately to avoid popup blocking
    let paymentWindow;
    if (depositMethod === 'orange') {
      paymentWindow = window.open('', '_blank');
    }
    const amountVal = parseFloat(depositMGA);
    if (isNaN(amountVal) || amountVal <= 0) {
      toast.error('Montant invalide');
      return;
    }
    setDepositInitialMessage('');
    setDepositResultMessage('');
    setDepositState('processing');
    setDepositId(null);
    const idempotencyKey = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    try {
      // Build payload with selected method
      const payload = { amount_mga: amountVal, method: depositMethod, idempotency_key: idempotencyKey };
      if (depositMethod === 'manual') {
        payload.reference = depositNumber;
      }
      const res = await API.post('/deposits/', payload);

      if (depositMethod === 'mvola') {
        const msg = res.data.message || 'Veuillez entrer votre code secret sur votre téléphone dans les 30 secondes.';
        setDepositInitialMessage(msg);
        setDepositId(res.data.deposit_id);
        setDepositState('processing');
        toast.success(msg);
      } else if (depositMethod === 'manual') {
        const msg = res.data.message;
        setDepositInitialMessage(msg);
        setDepositId(res.data.deposit_id);
        setDepositState('processing');
        toast.success(msg);
      } else {
        // Orange Money
        const paymentUrl = res.data.payment_url;
        // Navigate the pre-opened window or fallback
        if (paymentWindow) {
          paymentWindow.location.href = paymentUrl;
        } else {
          window.open(paymentUrl, '_blank');
        }
        const orangeMsg = 'Paiement Orange Money initié. Veuillez compléter dans la nouvelle fenêtre.';
        setDepositInitialMessage(orangeMsg);
        setDepositId(res.data.deposit_id);
        setDepositState('processing');
        toast.success(orangeMsg);
      }
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data.error) {
        toast.error(err.response.data.error);
      } else {
        toast.error('Échec de la demande de dépôt');
      }
      setDepositState('idle');
    }
    // Reset inputs
    setDepositMGA('');
  };

  const isProviderMethodDown =
    (depositMethod === 'mvola' && methodStatus.mvola === 0) ||
    (depositMethod === 'orange' && methodStatus.orange === 0);
  const isManualMethodUnavailable = depositMethod === 'manual' && !isManualAvailable;
  const isSelectedMethodUnavailable = isProviderMethodDown || isManualMethodUnavailable;
  const selectedMethodUnavailableMessage = isManualMethodUnavailable
    ? 'Le dépôt MVola manuel est indisponible le samedi et disponible uniquement entre 08h et 22h les autres jours. Veuillez utiliser Orange Money ou MVola instantané.'
    : 'Ce moyen de paiement est momentanément indisponible. Veuillez choisir une autre méthode de paiement.';
  const depositButtonLabel = needsMvolaNumberRegistration
    ? (isSavingMvolaNumber ? 'Enregistrement...' : 'Enregistrer le numéro MVola')
    : 'Continuer';

  return (
    <section className="deposit-section beautiful-deposit-section">
      <div className="deposit-card">
        <div className="deposit-modern-header">
          <div className="deposit-title-block">
            <h2>Dépôt sur le compte</h2>
            <p>Alimentez votre solde en Ariary avec votre service Mobile Money.</p>
          </div>
        </div>
        <div className={`deposit-right${depositMethod === 'mvola' ? ' expand-form' : ''}`}>
          {depositState === 'idle' && (
            <form
              className="deposit-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (needsMvolaNumberRegistration) {
                  handleSaveMvolaNumber();
                } else {
                  handleDeposit();
                }
              }}
            >
              <label className="deposit-label">
                <span className="deposit-field-heading">
                  <span className="deposit-field-label">Méthode de paiement</span>
                  <span className="deposit-field-helper">Choisissez votre service</span>
                </span>
                <div className="custom-dropdown">
                  <div 
                    className={`dropdown-selected ${dropdownMethodOpen ? 'open' : ''}`} 
                    onClick={() => setDropdownMethodOpen(!dropdownMethodOpen)}
                    role="button"
                    tabIndex="0"
                    aria-haspopup="listbox"
                    aria-expanded={dropdownMethodOpen}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setDropdownMethodOpen(!dropdownMethodOpen);
                      }
                    }}
                  >
                    {depositMethod === 'orange' ? (
                      <span className="dropdown-option-content">
                        <img src={orangeLogo} alt="Orange Money" className="payment-logo dropdown" />
                        <span className="deposit-method-name">Orange Money</span>
                        <span className="fee-text">Sans frais</span>
                      </span>
                    ) : depositMethod === 'manual' ? (
                      <span className="dropdown-option-content">
                        <img src={mvolaLogo} alt="Mvola manuel" className="payment-logo dropdown" />
                        <span className="deposit-method-name">MVola manuel</span>
                        <span className="fee-text">Sans frais</span>
                        {!isManualAvailable && <span className="method-unavailable">Indisponible</span>}
                      </span>
                    ) : (
                      <span className="dropdown-option-content">
                        <img src={mvolaLogo} alt="MVola instantané" className="payment-logo dropdown" />
                        <span className="deposit-method-name">MVola instantané</span>
                        <span className="fee-text">Frais 2,5 %</span>
                      </span>
                    )}
                  </div>
                  <div className={`dropdown-options ${dropdownMethodOpen ? 'open' : ''}`} role="listbox" aria-label="Méthodes de paiement">
                    <div 
                      className={`dropdown-option ${depositMethod === 'orange' ? 'selected' : ''}`}
                      onClick={() => {
                        setDepositMethod('orange');
                        setDropdownMethodOpen(false);
                      }}
                    >
                      <span className="dropdown-option-content">
                        <img src={orangeLogo} alt="Orange Money" className="payment-logo dropdown" />
                        <span className="deposit-method-name">Orange Money</span>
                        <span className="fee-text">Sans frais</span>
                      </span>
                    </div>
                    <div 
                      className={`dropdown-option ${depositMethod === 'mvola' ? 'selected' : ''}`}
                      onClick={() => {
                        setDepositMethod('mvola');
                        setDropdownMethodOpen(false);
                      }}
                    >
                      <span className="dropdown-option-content">
                        <img src={mvolaLogo} alt="MVola instantané" className="payment-logo dropdown" />
                        <span className="deposit-method-name">MVola instantané</span>
                        <span className="fee-text">Frais 2,5 %</span>
                      </span>
                    </div>
                    <div 
                      className={`dropdown-option ${depositMethod === 'manual' ? 'selected' : ''}`}  
                      style={!isManualAvailable ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      onClick={() => { 
                        setDepositMethod('manual'); 
                        setDropdownMethodOpen(false); 
                      }}
                    >
                      <span className="dropdown-option-content">
                        <img src={mvolaLogo} alt="Mvola manuel" className="payment-logo dropdown" />
                        <span className="deposit-method-name">MVola manuel</span>
                        <span className="fee-text">Sans frais</span>
                        {!isManualAvailable && <span className="method-unavailable">Indisponible</span>}
                      </span>
                    </div>
                  </div>
                </div>
              </label>
              {isSelectedMethodUnavailable ? (
                <div className="deposit-error-display deposit-unavailable-message">
                  {selectedMethodUnavailableMessage}
                </div>
              ) : !needsMvolaNumberRegistration && (
              <label className="deposit-label">
                <span className="deposit-field-heading">
                  <span className="deposit-field-label">Montant à déposer</span>
                  <span className="deposit-field-helper">Minimum : 1 000 MGA</span>
                </span>
                <div className="deposit-input-container deposit-amount-container">
                  <input
                    type="number"
                    placeholder="0"
                    value={depositMGA}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDepositMGA(value);
                      // Only allow integer values
                      const isInteger = /^\d+$/.test(value);
                      const amountVal = parseInt(value, 10);
                      if (value && (!isInteger || isNaN(amountVal) || amountVal < 1000)) {
                        if (!isInteger) {
                          setDepositMGAError('Le montant doit être un entier');
                        } else {
                          setDepositMGAError('Le montant minimum est de 1000 MGA');
                        }
                      } else {
                        setDepositMGAError('');
                      }
                    }}
                    min="1000"
                    step="1"
                    required
                    className="deposit-input"
                  />
                  <span className="deposit-currency-suffix">MGA</span>
                </div>
                {depositMGAError && (
                  <div className="deposit-error-display">{depositMGAError}</div>
                )}
              </label>
              )}
              {depositMethod === 'mvola' && methodStatus.mvola !== 0 && (
                <label className="deposit-label">
                  <span className="deposit-field-heading">
                    <span className="deposit-field-label">Numéro MVola</span>
                    <span className="deposit-field-helper">Numéro utilisé pour vos dépôts</span>
                  </span>
                  {savedMvolaNumber ? (
                    <>
                      <input
                        type="text"
                        value={savedMvolaNumber}
                        readOnly
                        disabled
                        className="deposit-input"
                      />
                      <div className="deposit-error-display deposit-saved-number">
                        Ce numéro est enregistré, unique et ne peut pas être modifié.
                      </div>
                    </>
                  ) : (
                    <>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        placeholder="Ex. 0341234567"
                        value={depositNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!/^\d*$/.test(value)) {
                            setMvolaNumberError('Le numéro MVola doit contenir uniquement des chiffres.');
                            return;
                          }
                          if (value.length > 10) {
                            setMvolaNumberError('Le numéro MVola doit contenir exactement 10 chiffres.');
                            return;
                          }
                          setDepositNumber(value);
                          setMvolaNumberError(value ? getMvolaNumberError(value) : '');
                        }}
                        onBlur={() => setMvolaNumberError(getMvolaNumberError(depositNumber))}
                        required
                        className="deposit-input"
                        maxLength="10"
                        pattern="0[0-9]{9}"
                        title="Le numéro MVola doit commencer par 0 et contenir exactement 10 chiffres."
                      />
                      {mvolaNumberError && (
                        <div className="deposit-error-display">{mvolaNumberError}</div>
                      )}
                      <div className="deposit-info-display">
                        <span className="deposit-info-icon" aria-hidden="true">!</span>
                        <span>
                          Veuillez enregistrer le numéro MVola que vous utiliserez pour vos dépôts sur Adabo Cash. Vérifiez bien le numéro saisi avant de l'enregistrer, car vous ne pourrez plus le changer à l'avenir. Veuillez entrer un numéro inscrit à votre nom.
                        </span>
                      </div>
                    </>
                  )}
                </label>
              )}
              {depositMethod === 'manual' && (
                isManualAvailable && (
                  <>
                    <div className="manual-deposit-instruction">
                      <div>
                        <span>Veuillez transférer le montant au numéro suivant, puis entrez la référence de transfert ci-dessous pour vérification: 038 09 541 68 (Rariny Sitrakiniaina)</span>
                      </div>
                    </div>
                    <label className="deposit-label">
                      <span className="deposit-field-heading">
                        <span className="deposit-field-label">Référence de transfert</span>
                        <span className="deposit-field-helper">Indiquée dans votre SMS</span>
                      </span>
                      <input
                        type="text"
                        placeholder="Ex. 1234567890"
                        value={depositNumber}
                        onChange={(e) => setDepositNumber(e.target.value)}
                        required
                        className="deposit-input"
                      />
                    </label>
                  </>
                )
              )}
              <button
                type="submit"
                className="deposit-btn"
                disabled={
                  depositMGAError !== '' ||
                  isSelectedMethodUnavailable ||
                  (needsMvolaNumberRegistration ? (!MVOLA_NUMBER_PATTERN.test(depositNumber) || mvolaNumberError !== '' || isSavingMvolaNumber) : depositMethod === 'mvola' ? !depositMGA : depositMethod === 'manual' ? (!depositMGA || !depositNumber) : !depositMGA)
                }
              >
                <span>{depositButtonLabel}</span>
                {!isSavingMvolaNumber && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
              <div className="deposit-security-note">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 3l7 3v5c0 4.6-2.9 8.8-7 10-4.1-1.2-7-5.4-7-10V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Dépôt sécurisé avec suivi automatique de la transaction
              </div>
            </form>
          )}
          {depositState === 'processing' && (
            <div className="deposit-processing">
              <div className="spinner"></div>
              <h3>Vérification en cours</h3>
              <p className="processing-message">{depositInitialMessage && <span className="processing-message-with-initial">{depositInitialMessage}</span>}Vérification de votre transaction...</p>
            </div>
          )}
          {(depositState === 'success' || depositState === 'failed' || depositState === 'timeout') && (
            <div className={`deposit-result ${depositState}`}>
              <div className="result-icon">
                {depositState === 'success' ? (
                  <svg width="48" height="48" fill="none" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="#34a853"/><path d="M34 18l-12 12-6-6" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  <svg width="48" height="48" fill="none" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="#ea4335"/><path d="M30 18L18 30M18 18l12 12" stroke="#fff" strokeWidth="3" strokeLinecap="round"/></svg>
                )}
              </div>
              <h3>{depositState === 'success' ? 'Dépôt confirmé' : 'Échec du dépôt'}</h3>
              <p className="result-message">{depositResultMessage}</p>
              <button 
                onClick={() => { setDepositState('idle'); setDepositMGA(''); }} 
                className="deposit-btn result-button-styled"
              >
                Nouveau dépôt
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DepositPage;
